import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import {
  NotificationCampaign as Campaign,
  NotificationCampaignImageUploadResponse as ImageUploadResponse,
  NotificationCampaignListFilters as ListFilters,
  NotificationCampaignHistoryFilters as HistoryFilters,
  NotificationCampaignHistoryDetail as HistoryDetail,
  NotificationCampaignHistoryPage as HistoryPage,
  NotificationCampaignHistoryRecipientPage as HistoryRecipientPage,
  NotificationCampaignPage as CampaignPage,
  NotificationCampaignStatus as Status,
  SaveNotificationCampaignRequest as SaveCampaignRequest,
} from '../../domain/models/notifications/popup.model';
import { Env } from '../config/@env';
import { EnvMicroNotifications } from '../config/env.micro.notifications';
import { ENV } from '../config/env.token';
import { NOTIFICATIONS_ENV } from '../config/micros-env.tokens';

@Injectable({ providedIn: 'root' })
export class PopupsService {
  constructor(
    private readonly http: HttpClient,
    @Inject(ENV) private readonly env: Env,
    @Optional() @Inject(NOTIFICATIONS_ENV) private readonly notificationsEnv?: EnvMicroNotifications,
  ) {}

  list(filters: ListFilters = {}): Observable<CampaignPage> {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 6);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.date) params = params.set('date', filters.date);
    if (filters.time) params = params.set('time', filters.time);
    return this.http.get<CampaignPage>(this.baseUrl(), { params });
  }

  history(filters: HistoryFilters = {}): Observable<HistoryPage> {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 20);
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
    return this.http.get<HistoryPage>(`${this.baseUrl()}/history`, { params });
  }

  historyRecipients(id: string, page = 0, size = 20): Observable<HistoryRecipientPage> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size);
    return this.http.get<HistoryRecipientPage>(`${this.baseUrl()}/history/${id}/recipients`, { params });
  }

  historyDetail(id: string): Observable<HistoryDetail> {
    return this.http.get<HistoryDetail>(`${this.baseUrl()}/history/${id}`);
  }
  exportHistory(filters: HistoryFilters = {}): Observable<Blob | null> {
    let params = new HttpParams();
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
    return this.http.get(`${this.baseUrl()}/history/export`, { params, responseType: 'blob' }).pipe(
      catchError(() => of(null)),
    );
  }

  create(payload: SaveCampaignRequest): Observable<Campaign> {
    return this.http.post<Campaign>(this.baseUrl(), payload);
  }

  update(id: string, payload: SaveCampaignRequest): Observable<Campaign> {
    return this.http.patch<Campaign>(`${this.baseUrl()}/${id}`, payload);
  }

  changeStatus(id: string, status: Status): Observable<Campaign> {
    return this.http.patch<Campaign>(`${this.baseUrl()}/${id}/status`, { status });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl()}/${id}`);
  }

  uploadImage(file: File): Observable<ImageUploadResponse> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<ImageUploadResponse>(`${this.baseUrl()}/upload-image`, body);
  }

  findActive(targetPermissionId: number, targetRoute: string): Observable<Campaign[]> {
    const params = new HttpParams()
      .set('targetPermissionId', targetPermissionId)
      .set('targetRoute', targetRoute);
    return this.http.get<Campaign[]>(`${this.baseUrl()}/active`, { params });
  }

  markShown(id: string): Observable<void> {
    return this.action(id, 'shown');
  }

  dismiss(id: string): Observable<void> {
    return this.action(id, 'dismiss');
  }

  markClicked(id: string): Observable<void> {
    return this.action(id, 'click');
  }

  private action(id: string, action: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl()}/${id}/${action}`, null);
  }

  private baseUrl(): string {
    const config = this.notificationsEnv ?? this.env.micros.notifications ?? {
      host: 'http://localhost:8096',
      apiBasePath: '/notifications',
    };
    const rootUrl = `${config.host}${config.apiBasePath}`.replace(/\/$/, '');
    return rootUrl.replace(/\/notifications$/, '/popups');
  }
}
